/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a template will carry, said at the moment the operator can act on it** (`TM5`, `#1171`).
 *
 * The first draft of `TM5` was a *withholding* rule: strip values that look like secrets before
 * saving. Todd replaced it, 2026-08-26:
 *
 * > *"Even simpler may be to tell the user: 'Every field you save in this template will be included
 * > for users of the template, including any static values, terms, … stored in the template' — up to
 * > them to decide what they want to remove before the save."*
 *
 * Better on three counts, and the third kills the alternative:
 *
 * - It is the **`#1045` discipline** — state the effect, in the operator's terms, where they can act
 *   on it. *"Every value in this template travels with it"* is an outcome; a classifier that
 *   silently drops constants is a mechanism.
 * - It needs **no classifier to be right**. A withholding rule had to tell *my publisher id* from
 *   *the vendor's constant* on data the product cannot interpret, and one that errs toward
 *   withholding ships a template that silently does not work — `TM6`'s *invents no values* failing
 *   in reverse.
 * - **An account-scoped template should carry the operator's publisher id.** That is the point of
 *   *"Magnite, for Acme"*. A rule that strips it makes the common case worse to serve the rare one.
 *
 * So `visibility` stays the **scope** and does not gate content, and this is the mechanism.
 *
 * ## The statement is not decoration
 *
 * On the pre-pilot deployment the constants that would travel include `app.publisher.id`,
 * `app.content.id`, `ext.kvps.scor`, `ext.kvps.iris_id` and `ext.kvps.ttd_uid2` / `extra_eids` /
 * `givn` — long base64 values that look like real UID2/EID identity tokens captured from a sample
 * tag. **The operator can only remove what they are told is there**, which is why this names paths
 * and shows values rather than counting them.
 *
 * Separated from the dialog for the reason `clone-options.ts` is: the **decisions** are testable and
 * the rendering is not.
 */

import { templateToLeaves } from './editors/ortb-body-editor.adapters';
import { paramsDtoToRows } from './editors/parameter-editor.adapters';
import type { DemandSource } from '../../types';

/** One line of the disclosure — a plane, what it holds, and the literals worth looking at. */
export interface TemplateDisclosureLine {
  /** What this is, in the operator's words. */
  label: string;
  /** How much of it there is. */
  count: number;
  /**
   * The **literal** values that would travel, most specific first.
   *
   * Only static values appear. A macro or a path resolves per request, so it carries nothing of this
   * deployment's; a constant is the operator's own text and is the whole reason this list exists.
   */
  literals: readonly { path: string; value: string }[];
}

/** Values long enough to be an identity token rather than a setting — shown truncated, never hidden. */
const MAX_SHOWN = 6;
const MAX_VALUE_CHARS = 48;

function short(value: string): string {
  return value.length <= MAX_VALUE_CHARS ? value : `${value.slice(0, MAX_VALUE_CHARS)}…`;
}

/** A stored static value as the text an operator would recognise, or nothing if it is not one. */
function literalOf(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.length > 0 ? JSON.stringify(value) : undefined;
  if (value && typeof value === 'object') {
    return Object.keys(value).length > 0 ? JSON.stringify(value) : undefined;
  }
  return undefined;
}

/**
 * **Everything this source would hand to whoever uses the template.**
 *
 * Derived from the document rather than from a list of fields somebody wrote down — `TM2`'s rule
 * arriving at the disclosure: an enumeration here would describe whichever planes were remembered,
 * and the omission would be invisible precisely because the panel looks complete.
 */
export function describeTemplateContents(source: DemandSource): TemplateDisclosureLine[] {
  const lines: TemplateDisclosureLine[] = [];

  const rows = paramsDtoToRows(source.params);
  if (rows.length > 0) {
    lines.push({
      label: 'Request parameters',
      count: rows.length,
      literals: rows
        .filter((row) => row.mode === 'static')
        .map((row) => ({ path: row.name, value: literalOf(row.value) }))
        .filter((entry): entry is { path: string; value: string } => entry.value !== undefined)
        .map((entry) => ({ path: entry.path, value: short(entry.value) })),
    });
  }

  const leaves = templateToLeaves(source.ortbBodyTemplate);
  if (leaves.length > 0) {
    lines.push({
      label: 'Request body fields',
      count: leaves.length,
      literals: leaves
        .filter((leaf) => leaf.kind === 'static')
        .map((leaf) => ({ path: leaf.path.join('.'), value: literalOf(leaf.staticValue) }))
        .filter((entry): entry is { path: string; value: string } => entry.value !== undefined)
        .map((entry) => ({ path: entry.path, value: short(entry.value) })),
    });
  }

  const headers = Object.entries(source.headers ?? {});
  if (headers.length > 0) {
    lines.push({
      label: 'Request headers',
      count: headers.length,
      // **Header values are shown, and that is the point rather than an oversight.** A vendor auth
      // key is exactly the thing an operator must be told is travelling, and masking it here would
      // reproduce the withholding rule `TM5` replaced — one field at a time, invisibly.
      literals: headers
        .map(([name, value]) => ({ path: name, value: literalOf(value) }))
        .filter((entry): entry is { path: string; value: string } => entry.value !== undefined)
        .map((entry) => ({ path: entry.path, value: short(entry.value) })),
    });
  }

  if (source.endpoint) {
    lines.push({ label: 'Endpoint', count: 1, literals: [{ path: 'endpoint', value: short(source.endpoint) }] });
  }
  if (source.responseMapping && source.responseMapping.entries.length > 0) {
    lines.push({ label: 'Response mapping', count: source.responseMapping.entries.length, literals: [] });
  }
  if (source.customPixels && source.customPixels.length > 0) {
    lines.push({ label: 'Pixels', count: source.customPixels.length, literals: [] });
  }
  if (source.terms) {
    // Named explicitly because Todd named it: *"including any static values, terms, …"*. Commercial
    // terms travelling into a shared template is the least obvious of these and the most expensive.
    lines.push({ label: 'Commercial terms', count: 1, literals: [] });
  }

  return lines;
}

/** How many literals to render before the rest are summarised. Rendering decision, stated once. */
export const TEMPLATE_DISCLOSURE_LITERAL_LIMIT = MAX_SHOWN;

/**
 * The sentence above the list.
 *
 * Deliberately about **every** field rather than the ones below it: the list shows literals, and an
 * operator who read only the list would conclude that a macro-mapped row does not travel. It does —
 * what it carries is a mapping rather than a value.
 */
export const TEMPLATE_DISCLOSURE_HEADLINE =
  'Everything saved here travels to anyone who uses this template — every value, mapping, header and ' +
  'commercial term below. Remove anything you do not want to share before you save.';
